import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";

import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import {
  fetchWardsThunk,
} from "../../../store/slices/wardsSlice";

import {
  fetchBalanceReportThunk,
  clearBalanceReport,
} from "../../../store/slices/balanceReportSlice";

import {
  printTableReport,
} from "./reportPrintUtils";

import "./ReportPage.css";

const BalanceReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector(
    (s) => s.wards?.list || []
  );

  const {
    rows = [],
    loading,
    error,
  } = useSelector(
    (s) => s.balanceReport || {}
  );

  const [
    wardId,
    setWardId,
  ] = useState("");

  const [
    balanceType,
    setBalanceType,
  ] = useState("both");

  const [
    balance,
    setBalance,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    sortField,
    setSortField,
  ] = useState("");

  const [
    sortDirection,
    setSortDirection,
  ] = useState("asc");

  const [
    page,
    setPage,
  ] = useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(20);

  useEffect(() => {
    dispatch(
      fetchWardsThunk()
    );
  }, [dispatch]);

  const handleSort = (
    field
  ) => {
    setSortField(
      (currentField) => {
        if (
          currentField ===
          field
        ) {
          setSortDirection(
            (
              currentDirection
            ) =>
              currentDirection ===
              "asc"
                ? "desc"
                : "asc"
          );

          return currentField;
        }

        setSortDirection(
          "asc"
        );

        return field;
      }
    );

    setPage(1);
  };

  const renderSortIcon = (
    field
  ) => {
    if (
      sortField !== field
    ) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp
            size={11}
          />

          <ChevronDown
            size={11}
          />
        </span>
      );
    }

    return sortDirection ===
      "asc" ? (
      <ChevronUp
        size={14}
        className="ms-1"
      />
    ) : (
      <ChevronDown
        size={14}
        className="ms-1"
      />
    );
  };

  const filteredRows =
    useMemo(() => {
      const q = search
        .trim()
        .toLowerCase();

      const searchedRows =
        !q
          ? [...rows]
          : rows.filter(
              (r) => {
                const searchableText = `
                  ${r.home_id || ""}
                  ${r.qr_code || ""}
                  ${r.door_no || ""}
                  ${r.family_head || ""}
                  ${r.ward_name || ""}
                  ${r.mobile || ""}
                  ${r.water_balance || ""}
                  ${r.slrm_bal || ""}
                  ${r.balance || ""}
                  ${r.selected_balance || ""}
                `.toLowerCase();

                return searchableText.includes(
                  q
                );
              }
            );

      if (!sortField) {
        return searchedRows;
      }

      const numericFields = [
        "home_id",
        "mobile",
        "water_balance",
        "slrm_bal",
        "balance",
        "selected_balance",
      ];

      return [
        ...searchedRows,
      ].sort(
        (a, b) => {
          let aValue =
            a?.[
              sortField
            ];

          let bValue =
            b?.[
              sortField
            ];

          if (
            numericFields.includes(
              sortField
            )
          ) {
            aValue =
              Number(
                aValue || 0
              );

            bValue =
              Number(
                bValue || 0
              );

            return sortDirection ===
              "asc"
              ? aValue -
                  bValue
              : bValue -
                  aValue;
          }

          const comparison =
            String(
              aValue || ""
            ).localeCompare(
              String(
                bValue || ""
              ),
              undefined,
              {
                numeric: true,
                sensitivity:
                  "base",
              }
            );

          return sortDirection ===
            "asc"
            ? comparison
            : -comparison;
        }
      );
    }, [
      rows,
      search,
      sortField,
      sortDirection,
    ]);

  useEffect(() => {
    setPage(1);
  }, [
    wardId,
    balanceType,
    balance,
    search,
    rows.length,
    sortField,
    sortDirection,
  ]);

  const total =
    filteredRows.length;

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        total /
          pageSize
      )
    );

  const currentPage =
    Math.min(
      page,
      totalPages
    );

  const pagedRows =
    useMemo(() => {
      const start =
        (currentPage -
          1) *
        pageSize;

      return filteredRows.slice(
        start,
        start +
          pageSize
      );
    }, [
      filteredRows,
      currentPage,
      pageSize,
    ]);

  const handleLoad = () => {
    dispatch(
      fetchBalanceReportThunk({
        ward_id:
          wardId,

        balance_type:
          balanceType,

        balance,
      })
    );
  };

  const handleClear = () => {
    setWardId("");

    setBalanceType(
      "both"
    );

    setBalance("");

    setSearch("");

    setSortField("");

    setSortDirection(
      "asc"
    );

    setPage(1);

    dispatch(
      clearBalanceReport()
    );
  };

  const exportExcel = () => {
    let data = [];

    if (
      balanceType ===
      "both"
    ) {
      data =
        filteredRows.map(
          (
            r,
            idx
          ) => ({
            "Sl No.":
              idx + 1,

            "Qr Code":
              r.qr_code,

            "Door No":
              r.door_no,

            "Family Head":
              r.family_head,

            Ward:
              r.ward_name,

            Mobile:
              r.mobile,

            "Water Balance":
              r.water_balance,

            "SLRM Balance":
              r.slrm_bal,

            "Total Balance":
              r.balance,
          })
        );
    } else {
      const balanceLabel =
        balanceType ===
        "slrm"
          ? "SLRM Balance"
          : "Water Balance";

      data =
        filteredRows.map(
          (
            r,
            idx
          ) => ({
            "Sl No.":
              idx + 1,

            "Qr Code":
              r.qr_code,

            "Door No":
              r.door_no,

            "Family Head":
              r.family_head,

            Ward:
              r.ward_name,

            Mobile:
              r.mobile,

            [balanceLabel]:
              r.selected_balance,
          })
        );
    }

    const ws =
      XLSX.utils.json_to_sheet(
        data
      );

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Balance Report"
    );

    XLSX.writeFile(
      wb,
      `${balanceType}_balance_report.xlsx`
    );
  };

  const exportPDF = () => {
    let data = [];

    if (
      balanceType ===
      "both"
    ) {
      data =
        filteredRows.map(
          (
            r,
            idx
          ) => ({
            "Sl No.":
              idx + 1,

            "Qr Code":
              r.qr_code ??
              "",

            "Door No":
              r.door_no ??
              "",

            "Family Head":
              r.family_head ??
              "",

            Ward:
              r.ward_name ??
              "",

            Mobile:
              r.mobile ??
              "",

            "Water Balance":
              r.water_balance ??
              0,

            "SLRM Balance":
              r.slrm_bal ??
              0,

            "Total Balance":
              r.balance ??
              0,
          })
        );
    } else {
      const balanceLabel =
        balanceType ===
        "slrm"
          ? "SLRM Balance"
          : "Water Balance";

      data =
        filteredRows.map(
          (
            r,
            idx
          ) => ({
            "Sl No.":
              idx + 1,

            "Qr Code":
              r.qr_code ??
              "",

            "Door No":
              r.door_no ??
              "",

            "Family Head":
              r.family_head ??
              "",

            Ward:
              r.ward_name ??
              "",

            Mobile:
              r.mobile ??
              "",

            [balanceLabel]:
              r.selected_balance ??
              0,
          })
        );
    }

    const title =
      balanceType ===
      "water"
        ? "Water Balance Report"
        : balanceType ===
            "slrm"
          ? "SLRM Balance Report"
          : "Water & SLRM Balance Report";

    printTableReport({
      title,

      fileName:
        `${balanceType}_balance_report.pdf`,

      data,

      metaLines: [
        `Balance Type: ${
          balanceType ===
          "water"
            ? "Water"
            : balanceType ===
                "slrm"
              ? "SLRM"
              : "Both"
        }`,

        `Total Records: ${filteredRows.length}`,
      ],
    });
  };

  const renderPagination =
    () => {
      if (
        totalPages <= 1
      ) {
        return null;
      }

      const items = [];

      const maxButtons =
        5;

      let start =
        Math.max(
          1,
          currentPage -
            Math.floor(
              maxButtons /
                2
            )
        );

      let end =
        Math.min(
          totalPages,
          start +
            maxButtons -
            1
        );

      start =
        Math.max(
          1,
          end -
            maxButtons +
            1
        );

      items.push(
        <Pagination.Prev
          key="prev"
          disabled={
            currentPage ===
            1
          }
          onClick={() =>
            setPage(
              (p) =>
                Math.max(
                  1,
                  p - 1
                )
            )
          }
        />
      );

      for (
        let p =
          start;
        p <= end;
        p++
      ) {
        items.push(
          <Pagination.Item
            key={p}
            active={
              p ===
              currentPage
            }
            onClick={() =>
              setPage(
                p
              )
            }
          >
            {p}
          </Pagination.Item>
        );
      }

      items.push(
        <Pagination.Next
          key="next"
          disabled={
            currentPage ===
            totalPages
          }
          onClick={() =>
            setPage(
              (p) =>
                Math.min(
                  totalPages,
                  p + 1
                )
            )
          }
        />
      );

      return (
        <Pagination className="mb-0">
          {items}
        </Pagination>
      );
    };

  const sortableHeader = (
    label,
    field,
    style
  ) => (
    <th style={style}>
      <button
        type="button"
        className={`report-sort-btn ${
          sortField ===
          field
            ? "is-active"
            : ""
        }`}
        onClick={() =>
          handleSort(
            field
          )
        }
      >
        {label}

        {renderSortIcon(
          field
        )}
      </button>
    </th>
  );

  const reportTitle =
    balanceType ===
    "water"
      ? "Water Balance Report"
      : balanceType ===
          "slrm"
        ? "SLRM Balance Report"
        : "Water & SLRM Balance Report";

  const columnCount =
    balanceType ===
    "both"
      ? 9
      : 7;

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Balance Report
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={
                    wardId
                  }
                  onChange={(
                    e
                  ) =>
                    setWardId(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Select Ward
                  </option>

                  {wards.map(
                    (w) => (
                      <option
                        key={
                          w.ward_id
                        }
                        value={String(
                          w.ward_id
                        )}
                      >
                        {
                          w.ward_name
                        }
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Balance Type
                </Form.Label>

                <Form.Select
                  value={
                    balanceType
                  }
                  onChange={(
                    e
                  ) => {
                    setBalanceType(
                      e.target
                        .value
                    );

                    setPage(
                      1
                    );
                  }}
                >
                  <option value="both">
                    Both
                  </option>

                  <option value="water">
                    Water
                  </option>

                  <option value="slrm">
                    SLRM
                  </option>
                </Form.Select>
              </Col>

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Balance
                </Form.Label>

                <Form.Select
                  value={
                    balance
                  }
                  onChange={(
                    e
                  ) =>
                    setBalance(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Select Amount
                  </option>

                  <option value="lt_1000">
                    &lt; 1000
                  </option>

                  <option value="gte_1000">
                    &gt;= 1000
                  </option>

                  <option value="gte_2000">
                    &gt;= 2000
                  </option>

                  <option value="gte_5000">
                    &gt;= 5000
                  </option>
                </Form.Select>
              </Col>

              <Col
                lg={3}
                md={6}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              {reportTitle}{" "}

              <span className="report-count">
                (
                {total.toLocaleString()}{" "}
                records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search
                    size={16}
                  />
                </InputGroup.Text>

                <Form.Control
                  value={
                    search
                  }
                  onChange={(
                    e
                  ) =>
                    setSearch(
                      e.target
                        .value
                    )
                  }
                  placeholder="Search Door No / Name / Mobile..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={
                  exportExcel
                }
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />

                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={
                  exportPDF
                }
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />

                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    {sortableHeader(
                      "Sl No.",
                      "home_id",
                      {
                        width:
                          100,
                      }
                    )}

                    {sortableHeader(
                      "Qr Code",
                      "qr_code"
                    )}

                    {sortableHeader(
                      "Door No",
                      "door_no"
                    )}

                    {sortableHeader(
                      "Family Head",
                      "family_head"
                    )}

                    {sortableHeader(
                      "Ward",
                      "ward_name"
                    )}

                    {sortableHeader(
                      "Mobile",
                      "mobile"
                    )}

                    {balanceType ===
                    "both" ? (
                      <>
                        {sortableHeader(
                          "Water Balance",
                          "water_balance"
                        )}

                        {sortableHeader(
                          "SLRM Balance",
                          "slrm_bal"
                        )}

                        {sortableHeader(
                          "Total Balance",
                          "balance"
                        )}
                      </>
                    ) : balanceType ===
                      "slrm" ? (
                      sortableHeader(
                        "SLRM Balance",
                        "slrm_bal"
                      )
                    ) : (
                      sortableHeader(
                        "Water Balance",
                        "water_balance"
                      )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={
                          columnCount
                        }
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map(
                      (
                        r,
                        idx
                      ) => (
                        <tr
                          key={String(
                            r.home_id
                          )}
                        >
                          <td>
                            {(currentPage -
                              1) *
                              pageSize +
                              idx +
                              1}
                          </td>

                          <td>
                            {
                              r.qr_code
                            }
                          </td>

                          <td>
                            {
                              r.door_no
                            }
                          </td>

                          <td>
                            {
                              r.family_head
                            }
                          </td>

                          <td>
                            {
                              r.ward_name
                            }
                          </td>

                          <td>
                            {
                              r.mobile
                            }
                          </td>

                          {balanceType ===
                          "both" ? (
                            <>
                              <td>
                                {
                                  r.water_balance
                                }
                              </td>

                              <td>
                                {
                                  r.slrm_bal
                                }
                              </td>

                              <td>
                                {
                                  r.balance
                                }
                              </td>
                            </>
                          ) : balanceType ===
                            "slrm" ? (
                            <td>
                              {
                                r.slrm_bal
                              }
                            </td>
                          ) : (
                            <td>
                              {
                                r.water_balance
                              }
                            </td>
                          )}
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          columnCount
                        }
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  onChange={(
                    e
                  ) => {
                    setPage(
                      1
                    );

                    setPageSize(
                      Number(
                        e.target
                          .value
                      )
                    );
                  }}
                  className="page-size-select"
                >
                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (n) => (
                      <option
                        key={
                          n
                        }
                        value={
                          n
                        }
                      >
                        {n}
                      </option>
                    )
                  )}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page{" "}
                {currentPage}{" "}
                /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BalanceReportPage;